// frontend/src/pages/Dashboard.tsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowDown, ArrowUp, ArrowUpDown } from "lucide-react";
import { formatDecimal, formatPercent, formatDate, formatNumber } from "@/utils/format";
import { usePlanGuard } from "@/utils/use-plan-guard";
import { showToast } from "../utils/toast";
import Button from "@/components/ui/Button";
import api from "@/utils/axios";
import { RowActionsMenu } from "@/components/RowActionsMenu";


type ProjectRow = {
  project_id: number;
  name: string;
  country: string | null;
  type: string | null;
  capacity: number | null;
  relative_costs: number | null;
  irr_project_unlevered_pre_tax: number | null;
  irr_equity_levered_pre_tax: number | null;
  phase: string | null;
  last_changed: string; // ISO timestamp
};

type SortColumn =
  | "name"
  | "country"
  | "type"
  | "capacity"
  | "investment"
  | "irr_project_unlevered_pre_tax"
  | "irr_equity_levered_pre_tax"
  | "phase"
  | "last_changed";

type SortDirection = "asc" | "desc";

type ColumnType = "string" | "number" | "date";

type SortableColumn = {
  key: SortColumn;
  labelKey: string;
  type: ColumnType;
};

const SORTABLE_COLUMNS: SortableColumn[] = [
  { key: "name", labelKey: "dashboard.columns.name", type: "string" },
  { key: "country", labelKey: "dashboard.columns.country", type: "string" },
  { key: "type", labelKey: "dashboard.columns.type", type: "string" },
  { key: "capacity", labelKey: "dashboard.columns.capacity", type: "number" },
  { key: "investment", labelKey: "dashboard.columns.investment", type: "number" },
  { key: "irr_project_unlevered_pre_tax", labelKey: "dashboard.columns.irr_project", type: "number" },
  { key: "irr_equity_levered_pre_tax", labelKey: "dashboard.columns.irr_equity", type: "number" },
  { key: "phase", labelKey: "dashboard.columns.phase", type: "string" },
  { key: "last_changed", labelKey: "dashboard.columns.last_updated", type: "date" },
];

function getInvestment(project: ProjectRow): number | null {
  if (project.capacity == null || project.relative_costs == null) return null;
  return project.capacity * project.relative_costs;
}

function compareStrings(a: string | null, b: string | null): number {
  return (a ?? "").localeCompare(b ?? "", undefined, { sensitivity: "base" });
}

function compareNumbers(a: number | null, b: number | null): number {
  if (a == null && b == null) return 0;
  if (a == null) return 1;
  if (b == null) return -1;
  return a - b;
}

function compareByColumn(a: ProjectRow, b: ProjectRow, column: SortColumn): number {
  switch (column) {
    case "name":
      return compareStrings(a.name, b.name);
    case "country":
      return compareStrings(a.country, b.country);
    case "type":
      return compareStrings(a.type, b.type);
    case "capacity":
      return compareNumbers(a.capacity, b.capacity);
    case "investment":
      return compareNumbers(getInvestment(a), getInvestment(b));
    case "irr_project_unlevered_pre_tax":
      return compareNumbers(a.irr_project_unlevered_pre_tax, b.irr_project_unlevered_pre_tax);
    case "irr_equity_levered_pre_tax":
      return compareNumbers(a.irr_equity_levered_pre_tax, b.irr_equity_levered_pre_tax);
    case "phase":
      return compareStrings(a.phase, b.phase);
    case "last_changed":
      return compareStrings(a.last_changed, b.last_changed);
    default:
      return 0;
  }
}

function defaultDirection(type: ColumnType): SortDirection {
  return type === "string" ? "asc" : "desc";
}

function arrowPointsDown(type: ColumnType, direction: SortDirection): boolean {
  return type === "string" ? direction === "asc" : direction === "desc";
}


export default function Dashboard() {
  const [projects, setProjects] = useState<ProjectRow[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [projectToDelete, setProjectToDelete] = useState<ProjectRow | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showLimitNote, setShowLimitNote] = useState(false);
  const [sortColumn, setSortColumn] = useState<SortColumn>("last_changed");
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");

  const { plan, FREE_MAX_PROJECTS, canAddProject } = usePlanGuard(projects.length);
  const limitReached = plan === "free" && !canAddProject;

  const { t } = useTranslation();
  const navigate = useNavigate();

  const sortedProjects = useMemo(() => {
    const rows = [...projects];
    rows.sort((a, b) => {
      const cmp = compareByColumn(a, b, sortColumn);
      const directed = sortDirection === "asc" ? cmp : -cmp;
      if (directed !== 0) return directed;

      const nameCmp = compareStrings(a.name, b.name);
      if (nameCmp !== 0) return nameCmp;
      return a.project_id - b.project_id;
    });
    return rows;
  }, [projects, sortColumn, sortDirection]);

  const handleSort = (column: SortColumn) => {
    if (sortColumn === column) {
      setSortDirection((direction) => (direction === "asc" ? "desc" : "asc"));
    } else {
      const col = SORTABLE_COLUMNS.find((c) => c.key === column);
      setSortColumn(column);
      setSortDirection(defaultDirection(col?.type ?? "string"));
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get<ProjectRow[]>("/api/projects");
        if (!cancelled) setProjects(data);
      } catch (err) {
        console.error("Failed to load projects:", err);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);


  const refreshProjects = async () => {
    setIsLoading(true);
    try {
      const { data } = await api.get<ProjectRow[]>("/api/projects");
      setProjects(data);
    } catch (err) {
      console.error("Failed to reload projects:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const guardLimit = (): boolean => {
    if (limitReached) {
      setShowLimitNote(true);
      showToast(t("dashboard.plan_limit_error"), "error");
      return false;
    }
    return true;
  };

  const handleDuplicate = async (projectId: number) => {
    if (!guardLimit()) return;
    try {
      await api.post(`/api/projects/${projectId}/duplicate`);
      showToast(t("dashboard.project_duplicate_success"), "success");
      await refreshProjects();
    } catch (e) {
      console.error(e);
      showToast(t("dashboard.project_duplicate_error"), "error");
    }
  };


  const handleDelete = async () => {
    if (!projectToDelete) return;
    setIsDeleting(true);
    try {
      await api.delete(`/api/projects/${projectToDelete.project_id}`, {
        headers: { "x-confirm-delete": "true" },
      });
      showToast(t("dashboard.project_delete_success", { name: projectToDelete.name }), "success");
      setProjectToDelete(null);
      await refreshProjects();
    } catch (e) {
      console.error(e);
      showToast(t("dashboard.project_delete_error"), "error");
      setProjectToDelete(null);
    } finally {
      setIsDeleting(false);
    }
  };


  const handleNewProject = async () => {
    if (!guardLimit()) return;
    try {
      await api.post("/api/projects/new");
      showToast(t("dashboard.project_create_success"), "success");
      await refreshProjects();
    } catch (e) {
      console.error(e);
      showToast(t("dashboard.project_create_error"), "error");
    }
  };


  const handleAddSampleProject = async () => {
    if (!guardLimit()) return;
    try {
      await api.post("/api/projects/sample");
      showToast(t("dashboard.sample_add_success"), "success");
      await refreshProjects();
    } catch (e) {
      console.error(e);
      showToast(t("dashboard.sample_add_error"), "error");
    }
  };


  const handleImport = async () => {
    showToast(t("dashboard.feature_missing"), "info");
  };
 

  return (
    <div className="max-w-6xl mx-auto p-4 text-sm">
      <div className="flex justify-between items-center mb-4">
        <div className="flex gap-2">
          <Button
            onClick={() => handleNewProject()}
            variant="blue"
            className="inline-flex items-center gap-2 text-xs"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
            </svg>
            {t("dashboard.new_project")}
          </Button>
          <Button
            onClick={() => handleAddSampleProject()}
            variant="gray"
            className="inline-flex items-center gap-2 text-xs"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            {t("dashboard.sample_project")}
          </Button>

          {//<button onClick={() => handleImport()} className="bg-gray-200 hover:bg-gray-300 text-xs px-4 py-1 rounded">{t("dashboard.import")}</button>
          }
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full table-auto border border-gray-200 shadow-sm text-xs">
          <thead className="bg-gray-200 text-left">
            <tr>
              {SORTABLE_COLUMNS.map((col) => {
                const isActive = sortColumn === col.key;
                return (
                  <th
                    key={col.key}
                    scope="col"
                    aria-sort={isActive ? (sortDirection === "asc" ? "ascending" : "descending") : undefined}
                    className="px-3 py-2 border-b border-gray-300 whitespace-nowrap"
                  >
                    <button
                      type="button"
                      onClick={() => handleSort(col.key)}
                      aria-label={t("dashboard.sort_by", { column: t(col.labelKey) })}
                      className={`inline-flex items-center gap-1 bg-transparent border-0 p-0 font-inherit text-left cursor-pointer hover:text-elani-blue ${
                        isActive ? "text-elani-blue" : "text-gray-700"
                      }`}
                    >
                      <span>{t(col.labelKey)}</span>
                      {isActive ? (
                        arrowPointsDown(col.type, sortDirection) ? (
                          <ArrowDown className="w-3.5 h-3.5 shrink-0 text-elani-blue" aria-hidden="true" />
                        ) : (
                          <ArrowUp className="w-3.5 h-3.5 shrink-0 text-elani-blue" aria-hidden="true" />
                        )
                      ) : (
                        <ArrowUpDown className="w-3.5 h-3.5 shrink-0 text-gray-400" aria-hidden="true" />
                      )}
                    </button>
                  </th>
                );
              })}
              <th scope="col" className="px-3 py-2 border-b border-gray-300 whitespace-nowrap" />
            </tr>
          </thead>
          <tbody>
            {sortedProjects.map((project) => (
                <tr
                  key={project.project_id}
                  role="link"
                  tabIndex={0}
                  onClick={() => navigate(`/project/${project.project_id}`)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      navigate(`/project/${project.project_id}`);
                    }
                  }}
                  className="hover:bg-gray-100 transition-colors cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-elani-orange"
                >
                  <td className="px-3 py-2 border-b">{project.name}</td>
                  <td className="px-3 py-2 border-b">{project.country}</td>
                  <td className="px-3 py-2 border-b">{project.type}</td>
                  <td className="px-3 py-2 border-b">{formatDecimal(project.capacity)}</td>
                  <td className="px-3 py-2 border-b">
                    {project.capacity != null && project.relative_costs != null
                      ? formatNumber(project.capacity * project.relative_costs)
                      : "-"}
                  </td>
                  <td className="px-3 py-2 border-b">{formatPercent(project.irr_project_unlevered_pre_tax)}</td>
                  <td className="px-3 py-2 border-b">{formatPercent(project.irr_equity_levered_pre_tax)}</td>
                  <td className="px-3 py-2 border-b">{project.phase}</td>
                  <td className="px-3 py-2 border-b">{formatDate(project.last_changed)}</td>
                  <td className="px-3 py-2 border-b whitespace-nowrap">
                    <RowActionsMenu
                      onEdit={() => navigate(`/project/${project.project_id}`)}
                      onDuplicate={() => handleDuplicate(project.project_id)}
                      onDelete={() => setProjectToDelete(project)}
                    />
                  </td>
                </tr>
              )
            )}
          </tbody>
        </table>

        {isLoading ? (
          <p className="text-gray-700 text-xs px-4 pt-3">{t("dashboard.loading_projects")}</p>
        ) : projects.length === 0 ? (
          <p className="text-gray-700 text-xs px-4 pt-3">
            {t("dashboard.no_projects")}
          </p>
        ) : null} 

        {plan === "free" && showLimitNote && (
          <p id="plan-limit-note" className="mt-6 md:mt-8 text-xs text-gray-600">
            {t("dashboard.plan_limit_note", { count: FREE_MAX_PROJECTS })}
          </p>
        )}

      </div>

      {projectToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-40 z-50 flex items-center justify-center">
          <div className="bg-white p-6 rounded-lg shadow max-w-md w-full">
            <p className="mb-4">
              {t("dashboard.project_delete_confirm_title")}
            </p>
            <p className="mb-4">
              <strong>"{projectToDelete.name}"</strong>
            </p>
            <div className="flex justify-end space-x-2">
              <button
                onClick={() => setProjectToDelete(null)}
                className={`px-4 py-2 text-sm text-gray-800 rounded bg-gray-200 ${
                  isDeleting ? "cursor-not-allowed" : "hover:hover:bg-gray-300"
                }`}
              >
                {t("actions.cancel")}
              </button>
              <button
                onClick={handleDelete}
                disabled={isDeleting}
                className={`px-4 py-2 text-sm text-white rounded bg-elani-orange ${
                  isDeleting ? "cursor-not-allowed" : "hover:bg-elani-orange-dark"
                }`}
              >
                {isDeleting ? t("common.deleting") : t("actions.delete")}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
